import { hasAnswerChoices } from 'components/event_type_edit'

/**
 * @param {object | undefined} answered Invitee answer from GraphQL (`values`) or legacy (`value`).
 * @returns {string[]}
 */
const displayStringsFromInviteeAnswer = (answered) => {
  if (!answered) return []
  if (Array.isArray(answered.values)) {
    return answered.values.filter((v) => v !== undefined && v !== null && v !== '')
  }
  if (answered.value !== undefined && answered.value !== null && answered.value !== '') {
    return Array.isArray(answered.value) ? answered.value : [answered.value]
  }
  return []
}

/**
 * Maps stored invitee answers into BookModal question form `value` / `customValue` shape.
 */
const answerStringsToQuestionFormState = (field, answered) => {
  const displayValues = displayStringsFromInviteeAnswer(answered)
  const choices = field.answer_choices || []

  if (field.format === 'choices_many') {
    const value = []
    const customTexts = []
    for (const v of displayValues) {
      if (choices.includes(v)) {
        value.push(v)
      } else if (field.include_other) {
        if (!value.includes('custom_value')) value.push('custom_value')
        customTexts.push(String(v))
      } else {
        value.push(v)
      }
    }
    return { value, customValue: customTexts.join('\n') }
  }

  if (hasAnswerChoices(field.format)) {
    const first = displayValues[0]
    if (first === undefined || first === '') {
      return { value: undefined, customValue: '' }
    }
    if (choices.includes(first)) {
      return { value: first, customValue: '' }
    }
    if (field.include_other) {
      return { value: 'custom_value', customValue: String(first) }
    }
    return { value: first, customValue: '' }
  }

  if (!displayValues.length) {
    return { value: undefined, customValue: '' }
  }
  return { value: displayValues.join('\n'), customValue: '' }
}

/**
 * Builds BookModal question form state from event type custom fields and the
 * invitee's existing answers (host reschedule / BookModal context).
 */
export const mergeInviteeAnswersIntoQuestionFormValues = (eventType, inviteeEventFields) => {
  const customFields = eventType?.custom_fields
  if (!Array.isArray(customFields) || customFields.length === 0) return null

  // Answers join on `name`: each GraphQL invitee `eventFields[]` entry must use the same `name`
  // as `eventType.custom_fields[].name` (API contract).
  const byName = new Map(
    (inviteeEventFields || [])
      .filter((ef) => ef && ef.name)
      .map((ef) => [ef.name, ef])
  )

  return customFields
    .filter((field) => field.enabled)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((field) => {
      const answered = byName.get(field.name)
      const { value, customValue } = answerStringsToQuestionFormState(field, answered)

      return {
        ...field,
        answerChoices: field.answer_choices,
        includeOther: field.include_other,
        value,
        customValue: customValue || '',
      }
    })
}
